import { useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { meQuery, useOrg, useOrgId } from '@/app/session';
import { Avatar, Icon, IdChip, ImageryCredit, SiteImage, StageTrack } from '@/components/ui';
import { formatDateTime, formatTime } from '@/lib/format';
import { addressesOf, contactQuery, locationNameQuery, type Location, type Message } from './api';
import { membersQuery, useSenderIdentities } from './drafts';

/** The AI's mark where a person would have an avatar: who proposed a reply. */
export function AiMark({ size = 32 }: { size?: number }) {
  return (
    <span className="avatar avatar-ai" style={{ '--avatar-size': `${size}px` } as CSSProperties} aria-hidden>
      <Icon name="sparkle" size={Math.round(size * 0.5)} />
    </span>
  );
}

/**
 * Opens a site in the panel over the current conversation page (`?site=<id>`): same page, same
 * thread and draft underneath, other search params kept. Back closes it again.
 */
export function SiteLink({ locationId, className, children, title }: { locationId: string; className?: string; children: ReactNode; title?: string }) {
  return (
    <Link to="." search={(s) => ({ ...s, site: locationId, contact: undefined, company: undefined })} resetScroll={false} className={className} title={title}>
      {children}
    </Link>
  );
}

/** The current search without the site panel: closing it keeps the list's view and query. */
export function closedSitePanel<S extends object>(s: S): S & { site: undefined; contact: undefined; company: undefined } {
  return { ...s, site: undefined, contact: undefined, company: undefined };
}

/** Location name resolved through getLocation; falls back to the id chip while unknown. Opens the site panel. */
export function LocationName({ locationId, link = true }: { locationId: string; link?: boolean }) {
  const orgId = useOrgId();
  const q = useQuery(locationNameQuery(orgId, locationId));
  const content = q.data ? q.data.name : <IdChip id={locationId} label="Location" />;
  if (!link) return <>{content}</>;
  return <SiteLink locationId={locationId}>{content}</SiteLink>;
}

/** Job title and current company of a linked contact; nulls while unknown. */
export function useContact(contactId: string | null | undefined): { role: string | null; company: string | null } {
  const orgId = useOrgId();
  const q = useQuery({ ...contactQuery(orgId, contactId ?? ''), enabled: Boolean(contactId) });
  if (!q.data) return { role: null, company: null };
  const aff = q.data.affiliations.find((a) => !a.valid_to) ?? q.data.affiliations[0];
  return { role: q.data.job_title ?? aff?.role ?? null, company: aff?.company_name ?? null };
}

/** "Asset Manager, Liffey Industrial Holdings". */
export function roleLine(role: string | null | undefined, company: string | null | undefined): string | null {
  return [role, company].filter(Boolean).join(', ') || null;
}

/** The signed-in person, to label our own messages and drafts "You". */
export function useMe() {
  const me = useQuery(meQuery);
  const { actorId } = useOrg();
  const user = me.data?.user;
  return {
    actorId,
    name: user?.display_name ?? null,
    email: user?.verified_email?.toLowerCase() ?? null,
  };
}

/** Name of a person by actor id: "You", a teammate's name, or null when it cannot be read. */
export function useActorName(actorId: string | null | undefined): string | null {
  const orgId = useOrgId();
  const me = useMe();
  const members = useQuery(membersQuery(orgId));
  if (!actorId) return null;
  if (actorId === me.actorId) return 'You';
  return members.data?.get(actorId) ?? null;
}

const DAY = 86_400_000;

/** Mail list time: "14:45" today, "Tue" this week, "6 Oct" this year, else "6 Oct 2025". */
export function listTime(value: string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return formatTime(d);
  if (now.getTime() - d.getTime() < 6 * DAY && d < now) return d.toLocaleDateString(undefined, { weekday: 'short' });
  if (d.getFullYear() === now.getFullYear()) return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Mail client row time ("14:05", "Tue", "3 Mar") with the absolute time as a tooltip. */
export function RowTime({ value, className }: { value: string | null | undefined; className?: string }) {
  if (!value) return null;
  return (
    <time className={className ? `num ${className}` : 'num'} dateTime={value} title={formatDateTime(value)}>
      {listTime(value)}
    </time>
  );
}

/** Short time with the absolute value shown on hover (CSS swaps the two). */
export function HoverTime({ value }: { value: string | null }) {
  if (!value) return <span>Event date unknown</span>;
  const abs = formatDateTime(value);
  return (
    <time className="hover-time num" dateTime={value} title={abs}>
      <span className="hover-time-short">{listTime(value)}</span>
      <span className="hover-time-abs" aria-hidden>
        {abs}
      </span>
    </time>
  );
}

export interface Recipient {
  kind: 'to' | 'cc' | 'bcc';
  name: string;
  email: string;
  company?: string | null;
}

/** "To" and "Cc" lines of a letter: people as chips with their avatar; the address on hover. */
export function RecipientLines({ recipients }: { recipients: Recipient[] }) {
  const lines = (['to', 'cc', 'bcc'] as const)
    .map((kind) => [kind, recipients.filter((r) => r.kind === kind)] as const)
    .filter(([, rs]) => rs.length > 0);
  if (lines.length === 0) return <p className="paper-line paper-line-empty">No recipient yet</p>;
  return (
    <>
      {lines.map(([kind, rs]) => (
        <p key={kind} className="paper-line">
          <span className="paper-label">{kind === 'to' ? 'To' : kind === 'cc' ? 'Cc' : 'Bcc'}</span>
          {rs.map((r) => (
            <span key={r.email} className="person-chip" title={r.email}>
              <Avatar name={r.name} company={r.company} size={20} />
              {r.name}
            </span>
          ))}
        </p>
      ))}
    </>
  );
}

/** Email addresses that are us: sender identities, our outbound authors and the signed-in person. */
export function useOurAddresses(messages: Message[]): ReadonlySet<string> {
  const me = useMe();
  const senders = useSenderIdentities();
  return useMemo(() => {
    const set = new Set<string>(senders.identities.map((s) => s.email.toLowerCase()));
    if (me.email) set.add(me.email);
    for (const m of messages) if (m.kind === 'outbound_email') for (const a of addressesOf(m, 'from')) set.add(a.email_normalized);
    return set;
  }, [senders.identities, me.email, messages]);
}

/** The site behind the thread: a small satellite chip that opens the site panel over the thread. */
export function SiteChip({ location: l, credit }: { location: Location; credit: boolean }) {
  return (
    <SiteLink locationId={l.id} className="site-chip" title={`Open ${l.name}`}>
      <SiteImage lat={l.address.latitude} lon={l.address.longitude} zoom={16} width={44} height={44} label="" city={l.address.locality} credit={false} pin={false} />
      <span className="site-chip-text">
        <span className="site-chip-name">{l.name}</span>
        <StageTrack stage={l.pipeline_state} label={false} />
        {credit ? <ImageryCredit className="site-chip-credit" /> : null}
      </span>
    </SiteLink>
  );
}

const RAIL_KEY = 'eryce.conversations.rail';

/** The site rail beside a thread or compose: open or closed, remembered; open by default on wide screens. */
export function useRail(): [boolean, () => void] {
  const [open, setOpen] = useState<boolean>(() => {
    const saved = window.localStorage.getItem(RAIL_KEY);
    return saved ? saved === 'open' : window.innerWidth >= 1440;
  });
  const toggle = () =>
    setOpen((v) => {
      window.localStorage.setItem(RAIL_KEY, v ? 'closed' : 'open');
      return !v;
    });
  return [open, toggle];
}
